<template>
  <Transition name="fade">
    <div
      v-if="showing"
      class="fixed pt-24 pb-10 inset-0 w-full h-screen flex items-center justify-center bg-semi-75 z-20"
    >
      <div class="mx-2 w-full max-w-2xl max-h-full bg-white shadow-lg rounded-lg p-8 overflow-auto">
        <div class="w-full text-right sticky top-0 right-0">
          <button aria-label="close" class="text-xl text-gray-500" @click.prevent="close">×</button>
        </div>
        <div class="w-3/5 mx-auto">
          <div class="w-full flex pt-4 pb-2">
            <button
              v-on:click="filter = 'all', filteredComments"
              class="w-1/3 hover:bg-cdmx-blue-light font-semibold hover:text-white py-2 px-4 border-t border-l border-b border-cdmx-blue-dark hover:border-transparent rounded"
              v-bind:class="[filter === 'all' ? 'border-transparent bg-cdmx-blue-light text-white' : 'bg-transparent text-cdmx-blue-dark']"
            >Todos</button>
            <button
              v-on:click="filter = 'visible', filteredComments"
              class="w-1/3 hover:bg-cdmx-blue-light font-semibold hover:text-white py-2 px-4 border border-cdmx-blue-dark hover:border-transparent rounded"
              v-bind:class="[filter === 'visible' ? 'border-transparent bg-cdmx-blue-light text-white' : 'bg-transparent text-cdmx-blue-dark']"
            >Visibles</button>
            <button
              v-on:click="filter = 'hidden', filteredComments"
              class="w-1/3 hover:bg-cdmx-blue-light font-semibold hover:text-white py-2 px-4 border-t border-r border-b border-cdmx-blue-dark hover:border-transparent rounded"
              v-bind:class="[filter === 'hidden' ? 'border-transparent bg-cdmx-blue-light text-white' : 'bg-transparent text-cdmx-blue-dark']"
            >Ocultos</button>
          </div>
          <div
            v-for="comment in filteredComments"
            :key="comment.id"
            class="relative flex flex-col mb-4 px-4 py-2 bg-gray-200 rounded-lg shadow-lg"
          >
            <div class="flex items-center w-full">
              <div class="h-12 w-12 rounded-full overflow-hidden">
                <img
                  :src="'http://chappultepec.geoint.mx:3002/'+comment.profile_image"
                  @error="loadDefaultImg"
                  class="h-full w-full object-cover"
                />
              </div>
              <div class="ml-2">
                <div
                  class="text-sm text-gray-900 text-left ml-1"
                >{{ comment.name }} {{ comment.last_name }}</div>
                <div class="flex">
                  <template v-for="star in maxStars">
                    <svg
                      v-if="star <= comment.stars"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 fill-current text-yellow-600"
                      viewBox="0 0 24 24"
                      :key="star"
                    >
                      <path d="M0 0h24v24H0z" fill="none" />
                      <path
                        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                      />
                      <path d="M0 0h24v24H0z" fill="none" />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5 fill-current text-yellow-600"
                      viewBox="0 0 24 24"
                      :key="star"
                    >
                      <path
                        d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z"
                      />
                      <path d="M0 0h24v24H0z" fill="none" />
                    </svg>
                  </template>
                </div>
              </div>
              <div class="mt-2 flex-1 self-start text-xs text-right">
                <span
                  v-if="comment.status"
                  class="px-2 py-1 bg-white rounded-full border-2 border-cdmx-green"
                >Visible</span>
                <span
                  v-if="!comment.status"
                  class="px-2 py-1 bg-white rounded-full border-2 border-cdmx-red"
                >Oculto</span>
              </div>
            </div>
            <div class="flex flex-col">
              <div
                class="w-full text-right text-xs text-gray-800"
              >{{ comment.created | moment('DD/MM/YYYY hh:mm a') }}</div>
              <div class="w-full text-left text-gray-700">{{ comment.comment }}</div>
            </div>
            <div class="flex justify-end">
              <button
                @click="updateCommentStatus(comment.id, true, comment.id_point)"
                :disabled="comment.status"
                :class="{'cursor-not-allowed opacity-50': comment.status}"
                class="flex items-center justify-center h-10 w-10 mr-1 rounded-full bg-cdmx-green hover:bg-green-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8 fill-current text-white"
                  viewBox="0 0 24 24"
                >
                  <path fill="none" d="M0 0h24v24H0V0zm0 0h24v24H0V0z" />
                  <path
                    d="M16.59 7.58L10 14.17l-3.59-3.58L5 12l5 5 8-8zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
                  />
                </svg>
              </button>
              <button
                @click="updateCommentStatus(comment.id, false, comment.id_point)"
                :disabled="!comment.status"
                :class="{'cursor-not-allowed opacity-50': !comment.status}"
                class="flex items-center justify-center h-10 w-10 rounded-full bg-cdmx-red hover:bg-red-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8 fill-current text-white"
                  viewBox="0 0 24 24"
                >
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { backend_url } from "../config/url.config.ts";

export default {
  name: "place-comments-modal",
  data() {
    return {
      config: {
        headers: {
          "x-access-token": localStorage.getItem("accessToken")
        }
      },
      maxStars: 5,
      filter: "all"
    };
  },
  props: {
    showing: {
      required: true,
      type: Boolean
    },
    placeComments: {
      required: true
    }
  },
  watch: {
    showing(value) {
      if (value) {
        return document.querySelector("body").classList.add("overflow-hidden");
      }

      document.querySelector("body").classList.remove("overflow-hidden");
    }
  },
  methods: {
    close() {
      this.filter = "all";
      this.$emit("close");
    },
    updateCommentStatus(commentId, status, placeId) {
      this.axios
        .put(backend_url + "/api/comments_places/" + commentId, {
          status: status
        })
        .then(response => {
          this.$emit("updateComments");
        });
    },
    loadDefaultImg(event) {
      event.target.src =
        "https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png";
    }
  },
  computed: {
    filteredComments() {
      return this.placeComments.filter(comment => {
        switch (this.filter) {
          case "all":
            return true;
          case "visible":
            if (comment.status) {
              return true;
            } else {
              return false;
            }
          case "hidden":
            if (!comment.status) {
              return true;
            } else {
              return false;
            }
        }
      });
    }
  }
};
</script>

<style scoped>
.bg-semi-75 {
  background-color: rgba(0, 0, 0, 0.75);
}
</style>